import { jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "../../../entities/vehicleEquipment/api/api.js";
import "../../../shared/lib/store/createAppApi.js";
import "../../../shared/lib/store/createAppAsyncThunk.js";
import "../../../shared/lib/store/createAppSelector.js";
import "../../../shared/lib/store/createAppSlice.js";
import "redux";
import { useAppDispatch } from "../../../shared/lib/store/hooks/useAppDispatch.js";
import "../../../shared/lib/store/hooks/useAppSelector.js";
import "../../../shared/lib/store/hooks/useAppStore.js";
import "../../../entities/vehicleEquipment/model/selectors/selectOptionGroupsParameters.js";
import "../../../entities/vehicleEquipment/model/selectors/selectSpecificationGroupsParameters.js";
import "../../../entities/vehicleEquipment/model/selectors/selectSpecificationsParameters.js";
import { useGetVehiclesSpecificationGroups } from "../../../entities/vehicleEquipment/lib/useGetVehiclesSpecificationGroups.js";
import { useGetVehiclesSpecifications } from "../../../entities/vehicleEquipment/lib/useGetVehiclesSpecifications.js";
import { APP } from "../../../shared/config/app.js";
import "../../../shared/config/env.js";
import "../../../shared/config/events.js";
import "../../../shared/context/constructor.js";
import { useThisContext } from "../../../shared/context/this.js";
import "@perxis-auto/shared/lib";
import { useMergedConstructorItemProps } from "../../../shared/lib/constructor/hooks/useMergedConstructorItemProps.js";
import "../../../shared/lib/constructor/mergeConstructor.js";
import "@perxis-auto/shared";
import "@perxis-auto/ui-kit";
import "../../../shared/lib/logger/index.js";
import "react-router";
import "@perxis-auto/shared/lib/lib/is/isObject";
import { GET_DEFAULT_CONTAINER_PROPS } from "../config/props.js";
import { formatEquipmentGroups } from "../lib/formatEquipmentGroups.js";
import { formatSpecificationGroups } from "../lib/formatSpecificationGroups.js";
import { useEquipmentItems } from "../lib/useEquipmentItems.js";
import { EquipmentItem } from "../ui/equipmentItem/EquipmentItem.js";
import { actions } from "./slice.js";
const EQUIPMENT_SECTIONS = [
  {
    path: "baseOptions",
    title: "widgets.equipments.baseTitle"
  },
  {
    path: "extraOptions",
    title: "widgets.equipments.additionalTitle"
  },
  {
    path: "specifications",
    title: "widgets.equipments.specificationTitle"
  }
];
function getActiveGroupIds(groups, expandedIds) {
  return groups.filter((group) => expandedIds.includes(group.id)).map((group) => group.id);
}
const useContainer = (item) => {
  const props = useMergedConstructorItemProps(item, GET_DEFAULT_CONTAINER_PROPS);
  const { t } = useTranslation(APP.name);
  const dispatch = useAppDispatch();
  const vehicleItem = useThisContext();
  const vehicleId = vehicleItem == null ? void 0 : vehicleItem.id;
  const { data: specificationsDictionaryData } = useGetVehiclesSpecifications();
  const { data: specificationsGroupsDictionaryData } = useGetVehiclesSpecificationGroups();
  const equipmentItems = useEquipmentItems(vehicleId);
  const specificationsDictionary = specificationsDictionaryData == null ? void 0 : specificationsDictionaryData.items;
  const specificationsGroupsDictionary = specificationsGroupsDictionaryData == null ? void 0 : specificationsGroupsDictionaryData.items;
  const onGroupExpanded = useCallback(
    (expandedIds, groups) => {
      if (!vehicleId) {
        return;
      }
      const closedGroups = groups.filter((group) => !expandedIds.includes(group.id)).map((group) => group.id);
      const filteredEquipmentGroupIds = equipmentItems.filter((id) => !closedGroups.includes(id));
      const equipmentGroupSet = new Set(filteredEquipmentGroupIds);
      expandedIds.forEach((groupId) => equipmentGroupSet.add(groupId));
      dispatch(
        actions.upsertOne({
          id: vehicleId,
          groups: Array.from(equipmentGroupSet)
        })
      );
    },
    [vehicleId, dispatch, equipmentItems]
  );
  const specificationTabs = useMemo(() => {
    var _a;
    if (!((_a = vehicleItem == null ? void 0 : vehicleItem.data.specificationsValues) == null ? void 0 : _a.length) || !(specificationsDictionary == null ? void 0 : specificationsDictionary.length) || !(specificationsGroupsDictionary == null ? void 0 : specificationsGroupsDictionary.length)) {
      return [];
    }
    const specificationGroups = formatSpecificationGroups(
      vehicleItem.data.specificationsValues,
      specificationsDictionary,
      specificationsGroupsDictionary,
      t
    );
    const activeGroups = getActiveGroupIds(specificationGroups, equipmentItems);
    return [
      {
        key: EQUIPMENT_SECTIONS[2].path,
        label: t(EQUIPMENT_SECTIONS[2].title),
        children: /* @__PURE__ */ jsx(
          EquipmentItem,
          {
            groups: specificationGroups,
            activeGroups,
            onActiveGroupChange: (ids) => onGroupExpanded(ids, specificationGroups)
          }
        )
      }
    ];
  }, [
    equipmentItems,
    vehicleItem == null ? void 0 : vehicleItem.data.specificationsValues,
    onGroupExpanded,
    specificationsDictionary,
    specificationsGroupsDictionary,
    t
  ]);
  const optionTabs = useMemo(() => {
    if (!(vehicleItem == null ? void 0 : vehicleItem.data.options)) {
      return [];
    }
    const baseOptions = [];
    const extraOptions = [];
    vehicleItem.data.options.forEach((option) => {
      if (option.isBase) {
        baseOptions.push(option);
        return;
      }
      extraOptions.push(option);
    });
    const baseOptionGroups = formatEquipmentGroups(baseOptions);
    const extraOptionGroups = formatEquipmentGroups(extraOptions);
    const baseActiveGroups = getActiveGroupIds(baseOptionGroups, equipmentItems);
    const extraActiveGroups = getActiveGroupIds(extraOptionGroups, equipmentItems);
    const baseTab = baseOptionGroups.length ? {
      key: EQUIPMENT_SECTIONS[0].path,
      label: t(EQUIPMENT_SECTIONS[0].title),
      children: /* @__PURE__ */ jsx(
        EquipmentItem,
        {
          groups: baseOptionGroups,
          activeGroups: baseActiveGroups,
          onActiveGroupChange: (ids) => onGroupExpanded(ids, baseOptionGroups)
        }
      )
    } : null;
    const extraTab = extraOptionGroups.length ? {
      key: EQUIPMENT_SECTIONS[1].path,
      label: t(EQUIPMENT_SECTIONS[1].title),
      children: /* @__PURE__ */ jsx(
        EquipmentItem,
        {
          groups: extraOptionGroups,
          activeGroups: extraActiveGroups,
          onActiveGroupChange: (ids) => onGroupExpanded(ids, extraOptionGroups)
        }
      )
    } : null;
    const tabs2 = [];
    if (baseTab) {
      tabs2.push(baseTab);
    }
    if (extraTab) {
      tabs2.push(extraTab);
    }
    return tabs2;
  }, [equipmentItems, vehicleItem == null ? void 0 : vehicleItem.data.options, onGroupExpanded, t]);
  const tabs = useMemo(() => {
    return [...optionTabs, ...specificationTabs];
  }, [optionTabs, specificationTabs]);
  const isHidden = !vehicleItem || !vehicleItem.data || !tabs.length;
  return {
    ...props,
    items: tabs,
    isHidden
  };
};
export {
  useContainer
};
